import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getTargetVsActualForConfig,
  createTargetVsActualLogs,
  captureVehicleWeight,
  getWeighBridgeWorkCenters,
  getTareWeightHistoryForVehicleNumber,
} from 'src/redux/Actions/VehicleTracking';
import { useParams } from 'react-router-dom';

export default function useTargetVsActualFormLogic(
  selectedTargetVsActualConfig,
) {
  const dispatch = useDispatch();
  const { workflowStageId, vehicleTrackingNumber } = useParams();

  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const targetVsActualData = vehicleTrackingDataReducer?.targetVsActualItems;
  const tareWeightHistoryForVehicleNumber =
    vehicleTrackingDataReducer?.tareWeightHistoryForVehicleNumber;

  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;

  const [captureWeight, setCaptureWeight] = useState(false);
  const [captureWeightButtonAnimation, setCaptureWeightButtonAnimation] =
    useState(false);
  const [invalidFormSubmission, setInvalidFormSubmission] = useState(false);

  // Modal state for weight bridge options
  const [showWeightBridgeModal, setShowWeightBridgeModal] = useState(false);
  const [selectedWeightBridge, setSelectedWeightBridge] = useState(null);
  const [weightBridgeEventUUID, setWeightBridgeEventUUID] = useState(null);

  const { weighBridgeWorkCenters } = vehicleTrackingDataReducer;
  const isTareWeightCapture =
    selectedTargetVsActualConfig?.is_weight &&
    selectedTargetVsActualConfig?.field_name?.toLowerCase() === 'tare weight';

  useEffect(() => {
    if (!selectedTargetVsActualConfig?.id) return;
    dispatch(
      getTargetVsActualForConfig(
        organisationId,
        vehicleTrackingNumber,
        selectedTargetVsActualConfig?.id,
      ),
    );

    dispatch(getWeighBridgeWorkCenters(organisationId, '1'));

    if (isTareWeightCapture) {
      dispatch(
        getTareWeightHistoryForVehicleNumber(
          organisationId,
          vehicleTrackingNumber,
        ),
      );
    }
  }, [
    dispatch,
    organisationId,
    selectedTargetVsActualConfig?.id,
    isTareWeightCapture,
    vehicleTrackingNumber,
  ]);

  useEffect(() => {
    let attemptCounter = 0;
    if (captureWeight) {
      const checkTargetVsActual = () => {
        dispatch(
          getTargetVsActualForConfig(
            organisationId,
            vehicleTrackingNumber,
            selectedTargetVsActualConfig?.id,
            weightBridgeEventUUID,
          ),
        ).then((data) => {
          const hasNullTargetValue = data.some(
            (item) => item.target_value === null,
          );
          if (hasNullTargetValue && attemptCounter < 5) {
            attemptCounter += 1;
            setTimeout(checkTargetVsActual, 5000);
          } else {
            setCaptureWeight(false);
            setCaptureWeightButtonAnimation(false);
          }
        });
      };
      setCaptureWeightButtonAnimation(true);
      checkTargetVsActual();
    }

    return () => setCaptureWeight(false);
  }, [captureWeight, selectedTargetVsActualConfig?.id]);

  const handleCaptureWeight = (e) => {
    e.preventDefault();
    // If only one weight bridge option, use it directly without showing modal
    if (weighBridgeWorkCenters.length === 1) {
      handleWeightBridgeSelectionSubmit(weighBridgeWorkCenters[0]);
    } else if (weighBridgeWorkCenters.length > 1) {
      // Multiple options - show modal for selection
      setShowWeightBridgeModal(true);
    }
    // If no options available, do nothing (fail silently)
  };

  // const handleCaptureWeight = (e) => {
  //   e.preventDefault();
  //   setCaptureWeight(true);
  //   dispatch(
  //     captureVehicleWeight(
  //       organisationId,
  //       vehicleTrackingNumber,
  //       selectedTargetVsActualConfig?.field_name,
  //       selectedTargetVsActualConfig?.table_name,
  //       workflowStageId,
  //       null,
  //     ),
  //   );
  // };

  function handleWeightBridgeSelectionSubmit(optionToUse = null) {
    const selectedOption = optionToUse ?? selectedWeightBridge;

    if (!selectedOption) return;

    setShowWeightBridgeModal(false);
    setCaptureWeight(true);

    // generate new UUID for event check
    const uuid = crypto.randomUUID();
    setWeightBridgeEventUUID(uuid);

    // Now proceed with the actual weight capture
    dispatch(
      captureVehicleWeight(
        organisationId,
        vehicleTrackingNumber,
        selectedTargetVsActualConfig?.field_name,
        selectedTargetVsActualConfig?.table_name,
        workflowStageId,
        selectedOption?.id,
        uuid,
      ),
    );
  }

  const handleCloseModal = () => {
    setShowWeightBridgeModal(false);
    setSelectedWeightBridge(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const items = [];
    let invalid = false;

    targetVsActualData?.forEach((item) => {
      const actualValue = selectedTargetVsActualConfig?.is_weight
        ? item.target_value ?? ''
        : document.getElementsByName(`actual_${item.id}`)[0]?.value;
      const remarkValue = document.getElementsByName(`remark_${item.id}`)[0]
        ?.value;

      if (!selectedTargetVsActualConfig?.is_weight && actualValue === '') {
        invalid = true;
      }

      if (actualValue !== '') {
        items.push({
          object_id: item.id,
          target_value: item.target_value,
          actual_value: actualValue,
          name: item.name,
          remarks: remarkValue,
        });
      }
    });

    if (invalid || items.length === 0) {
      setInvalidFormSubmission(true);
      return;
    }

    const data = {
      tenant_organisation_id: organisationId,
      workflow_stage_id: workflowStageId,
      config_id: selectedTargetVsActualConfig?.id,
      vehicle_tracking_number: vehicleTrackingNumber,
      table_name: selectedTargetVsActualConfig?.table_name,
      field_name: selectedTargetVsActualConfig?.field_name,
      target_vs_actual_items: items,
    };

    dispatch(createTargetVsActualLogs(data)).then(() => {
      window.location.reload();
    });
  };

  return {
    targetVsActualData,
    tareWeightHistoryForVehicleNumber,
    isTareWeightCapture,
    themeColor,
    captureWeight,
    captureWeightButtonAnimation,
    handleCaptureWeight,
    handleSubmit,
    invalidFormSubmission,
    setInvalidFormSubmission,
    showWeightBridgeModal,
    setSelectedWeightBridge,
    selectedWeightBridge,
    weighBridgeWorkCenters,
    handleWeightBridgeSelectionSubmit,
    handleCloseModal,
  };
}
